import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@app/components/ui/Toast/toast'
import { DATADOG_ACTIONS } from '@app/app-constants'
import { logDataDogAction } from '@app/utils/logger'
import { DOCUMENTS_QUERY_KEY, FOLDERS_QUERY_KEY } from '../documents.constants'
import {
  CreateDocumentPayload,
  createDocument,
  updateDocumentPermissions,
} from '../service/documents.service'
import { addDocumentToFolder } from '../service/collections.service'
import { uploadDocumentFile } from '../service/documents-upload.service'
import { DocumentFileEntry, DocumentUploadFormData } from '../documents-upload.types'

export type FileUploadStatus = 'idle' | 'uploading' | 'success' | 'error'

export interface FileUploadProgress {
  file: File
  title: string
  status: FileUploadStatus
  errorMessage: string | null
}

type UploadDocumentsArgs = {
  form: DocumentUploadFormData
  onFileStatusChange: (fileIndex: number, status: FileUploadStatus, errorMessage?: string) => void
}

async function uploadOneFile(entry: DocumentFileEntry, form: DocumentUploadFormData) {
  const uploaded = await uploadDocumentFile(entry.file)

  const payload: CreateDocumentPayload = {
    title: entry.title,
    description_md: form.descriptionMd || null,
    // form.category stays a plain string in the form (not the payload's literal-union type) so
    // the Select can start unselected (''), but it's validated non-empty before submit is
    // reachable, so this cast is safe.
    category: form.category as CreateDocumentPayload['category'],
    source: form.source || null,
    organisation: form.organisation || null,
    published_at: form.publishedAt || null,
    notes_md: form.notesMd || null,
    tags: form.tags,
    georeference_type: form.georeference.type,
    geometry: form.georeference.geometry,
    visibility: form.visibility,
    file_key: uploaded.file_key,
    original_filename: uploaded.original_filename,
  }

  const created = await createDocument(payload)

  if (form.visibility === 'organisation' && form.roleIds.length > 0) {
    await updateDocumentPermissions(created.id, { user_roles: form.roleIds })
  }

  if (form.collectionId) {
    await addDocumentToFolder(form.collectionId, created.id)
  }

  return created
}

// Each file uploads and registers independently — one failure in a batch shouldn't roll back
// the others.
export const useUploadDocuments = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ form, onFileStatusChange }: UploadDocumentsArgs) => {
      const results = await Promise.allSettled(
        form.files.map(async (entry, index) => {
          onFileStatusChange(index, 'uploading')
          try {
            const created = await uploadOneFile(entry, form)
            onFileStatusChange(index, 'success')
            logDataDogAction(DATADOG_ACTIONS.DOCUMENT_UPLOADED, {
              id: created.id,
              title: created.title,
              mimeType: created.file.mimeType,
            })
            return created
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Upload failed'
            onFileStatusChange(index, 'error', message)
            throw error
          }
        }),
      )

      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.length - succeeded
      return { succeeded, failed, total: results.length }
    },
    onSuccess: ({ succeeded, failed, total }) => {
      if (succeeded > 0) {
        queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY] })
      }
      if (failed === 0) {
        toast({
          description: `${succeeded} document${succeeded === 1 ? '' : 's'} uploaded.`,
          position: 'top-right',
          type: 'success',
        })
      } else {
        toast({
          description: `${succeeded} of ${total} documents uploaded — ${failed} failed.`,
          position: 'top-right',
          type: succeeded > 0 ? 'info' : 'error',
        })
      }
    },
  })
}
